import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Checkbox } from '@/components/ui/checkbox';
import { GenericCheckboxList, type CheckboxListFilter, type CheckboxListOption } from '@/styles/GenericCheckboxList';
import { Input } from '@/components/ui/input';
import { DateInput } from '@/components/ui/date-input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SaveChangesButton, CancelButton, CloseButton } from './StandardButtons';
import "@/design-system/typography.css";
import '@/design-system/forms.css';
import { cn } from '@/lib/utils';
import { GenericSearchableSelect } from './GenericSearchableSelect';
import { useProjectMembershipPrompt } from '@/hooks/useProjectMembershipPrompt';
import { colors } from '../design-system/colors';
import { INSUFFICIENT_PERMISSIONS_MESSAGE, registerOpenForm } from '@/lib/permission-errors';

export interface GenericFormField {
  name: string;
  label: string;
  type: 'text' | 'number' | 'email' | 'date' | 'time' | 'select' | 'multiselect' | 'textarea' | 'password' | 'checkbox';
  placeholder?: string;
  required?: boolean;
  /** Keeps `required` semantics (no blank option, validation) but hides the red
   *  asterisk -- for a NOT NULL select that is always preselected, so it can
   *  never actually be left blank. */
  hideRequiredMark?: boolean;
  /** Overrides the generic message shown when this required field is blank (see requiredFieldMessage). */
  requiredMessage?: string;
  options?: { label: string; value: string; description?: string; attributes?: Record<string, string[]> }[];
  /** multiselect only: filters that narrow the options by one of their `attributes` (e.g. a cost code). */
  filters?: CheckboxListFilter[];
  row?: number;
  colSpan?: number; // 1 = half width, 2 = full width, 0.5 = quarter width (requires 4-column grid)
  className?: string;
  disabled?: boolean;
  rowMaxWidth?: string; // Max width for the entire row (e.g., '66.666%')
  rows?: number; // textarea only: its height in rows (default: the standard minimum height)
  helperText?: string | React.ReactNode; // Helper text to display below the field
  labelPosition?: 'top' | 'left'; // Position of label relative to input: 'top' (default) or 'left'
  /** A text / number field that SHOWS this value and cannot be edited: a figure the form computes from something else (e.g. the total of its items). Not part of the form data. */
  displayValue?: string;
  /**
   * A select whose options are PEOPLE assigned to the document (assignee, vendor, client): the name of the form's project field. When the form is SAVED, the people picked here who are not members of the project
   * are asked ONCE (the shared "add to project?" popup); declining (or nobody here may add them) saves nothing.
   * The select is searchable. The server still validates every save.
   */
  membershipProjectField?: string;
}

/**
 * The ONE message for a blank required field: "Please select project" for a
 * dropdown, "Please fill in title" for anything typed. A field may override it
 * with `requiredMessage`.
 */
export function requiredFieldMessage(field: Pick<GenericFormField, 'label' | 'type' | 'requiredMessage'>): string {
  if (field.requiredMessage) return field.requiredMessage;
  const label = field.label.trim().toLowerCase();
  return field.type === 'select' ? `Please select ${label}` : `Please fill in ${label}`;
}

const isBlankValue = (value: unknown) => value == null || (Array.isArray(value) ? value.length === 0 : String(value).trim() === '');

/**
 * First required, enabled field that is blank in `data`, as its message --
 * or null. GenericForm uses it on submit; a form with its own footer actions
 * (e.g. "Create PO", "Email to Client", which save before acting) calls it
 * too, so they validate exactly like the Save button does.
 */
export function firstMissingRequiredMessage(fields: GenericFormField[], data: Record<string, any>): string | null {
  const missing = fields.find((f) => f.required && !f.disabled && isBlankValue(data[f.name]));
  return missing ? requiredFieldMessage(missing) : null;
}

export interface GenericFormProps {
  title: string;
  /** Replaces the default "Create <title>" / "Edit <title>" header text (e.g. "Save Comparison Sheet"). */
  headerTitle?: string;
  mode: 'create' | 'edit';
  fields: GenericFormField[];
  initialValues?: Record<string, any>;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  isOpen: boolean;
  /** For comments, attachments, etc. A function receives the form's CURRENT values (e.g. so the attachments section can follow the form's own Project field). */
  additionalSections?: React.ReactNode | ((formData: Record<string, any>) => React.ReactNode);
  error?: string | null; // Error message to display
  submitButtonText?: string; // Custom submit button text
  saving?: boolean; // Loading state for submit button
  onFieldChange?: (name: string, value: any, formData: Record<string, any>) => void; // Callback when any field changes
  customFooterButtons?: React.ReactNode; // Custom footer buttons (replaces default buttons)
  /** Extra buttons placed between Cancel and Save in the DEFAULT footer (e.g. "Create PO"); ignored when `customFooterButtons` is given. */
  footerActions?: React.ReactNode;
  customLayout?: React.ReactNode; // Custom layout (replaces default form layout)
  size?: 'default' | 'compact'; // Form size: default (large) or compact (small)
  compactWidthClassName?: string; // compact size only: the max-width class (default 'max-w-md'), e.g. 'max-w-2xl' for a wider small form
  hasUnsavedChanges?: () => boolean; // Optional callback to check for unsaved changes (for customLayout)
  bodyClassName?: string; // Custom className for the body section
  onDirtyChange?: (isDirty: boolean) => void; // Reports live dirty state upward, e.g. so a parent tab bar can warn before switching away
}

// Apply font and generic-form-field class for consistent focus ring
const inputStyles = "font-form-input generic-form-field";

/**
 * Single source of truth for the "required field" red asterisk. Every
 * form that renders its own fields via a `customLayout` (bypassing this
 * component's own automatic field rendering below) should import this
 * instead of re-typing `<span className="text-red-500">*</span>` by hand
 * -- found live (2026-09-22 QA sweep) that at least 4 fields across the
 * app were genuinely required (backend NOT NULL / explicit validation)
 * but silently missing the asterisk because the hand-copied JSX was
 * never added for that particular field, or was left over on a field
 * that stopped being required. One component means one place to get it
 * right.
 */
export function RequiredMark({ required }: { required?: boolean }) {
  if (!required) return null;
  return <span className="text-red-500"> *</span>;
}

export interface GenericSelectOption {
  label: string;
  value: string;
  /** For a non-selectable informational row (e.g. "No invoices available"),
   *  same as Radix's own SelectItem `disabled`. */
  disabled?: boolean;
}

export interface GenericSelectProps {
  id?: string;
  value: string;
  onValueChange: (value: string) => void;
  options: GenericSelectOption[];
  placeholder?: string;
  /** When true, no "Select..." item is offered -- matches RequiredMark's
   *  own `required` semantics: a required select can never be returned to
   *  unselected, so there's nothing to render for that case. */
  required?: boolean;
  disabled?: boolean;
  className?: string;
  /** For long option lists (country, assignee, project, etc.) that need a
   *  scrollable dropdown instead of pushing the page down. */
  contentClassName?: string;
  /** Overrides what <SelectValue> displays for the current selection --
   *  needed by the rare field (e.g. a phone country-code select) that
   *  shows something other than the matched option's own label. */
  renderValue?: React.ReactNode;
  /** Inline style passthrough for the trigger -- needed by the rare
   *  read-only/disabled select that uses a design-system color (e.g.
   *  `colors.formHeaderFooter`) Tailwind has no utility class for. */
  triggerStyle?: React.CSSProperties;
  /** 'default' (the standard padded/bordered field look, `inputStyles`)
   *  or 'compact' (no base style at all -- just whatever `className` you
   *  pass). Needed for a select rendered inside a dense, repeatable table
   *  row (line items in a bill/invoice/PO/budget/bid) that's deliberately
   *  borderless (`border-0 shadow-none h-8 ...`) -- `inputStyles`'
   *  `generic-form-field` CSS class can't be cleanly overridden by a
   *  Tailwind utility className, so those rows opt into 'compact' instead
   *  of fighting the default. */
  variant?: 'default' | 'compact';
  /** true: the user can also TYPE to narrow the list (GenericSearchableSelect, same look and the same `required` / placeholder rules).
   *  For long lists such as cost codes or vendors; the options' `disabled` flag is not supported there. */
  searchable?: boolean;
}

/**
 * Dropdown genérico (2026-09-22): the ONE Radix Select wrapper every
 * dropdown in the app should render through, so its styling, "Select..."
 * placeholder/clear-item behavior, and required-field handling can be
 * changed in exactly one place instead of ~44 hand-rolled copies across
 * ~24 files. This is the same rendering GenericForm's own automatic
 * `type: 'select'` fields use below -- extracted so a `customLayout` form
 * (or any standalone select outside GenericForm entirely) can reuse it
 * without re-implementing the "Select..." sentinel/clear-item logic
 * itself. See GENERIC_SELECT_CLEAR_VALUE's own comment for why that
 * sentinel exists at all (Radix rejects an empty-string SelectItem).
 */
/** A Project dropdown (by its field id) always reads exactly "Select" -- one rule for every form. */
const PROJECT_FIELD_ID = /^project(_id|Id)$/;

export function GenericSelect({
  id,
  value,
  onValueChange,
  options,
  placeholder,
  required,
  disabled,
  className,
  contentClassName,
  renderValue,
  triggerStyle,
  variant = 'default',
  searchable = false,
}: GenericSelectProps) {
  if (id && PROJECT_FIELD_ID.test(id)) placeholder = 'Select';
  if (searchable) {
    return (
      <GenericSearchableSelect
        id={id}
        value={value}
        onValueChange={onValueChange}
        options={options}
        placeholder={placeholder || 'Select'}
        // like the plain select: an optional one offers its "Select..." row to go back to nothing, a required one does not
        clearLabel={required ? undefined : placeholder || 'Select'}
        disabled={disabled}
        className={className}
      />
    );
  }
  return (
    <Select
      value={value || ''}
      onValueChange={(v) => {
        // Radix reports '' (it has no item with that value) when the select's value matches none of its options - e.g. a saved choice whose options are still
        // loading. That is not the user choosing "nothing" (that arrives as the clear item's sentinel below): passing it on silently emptied the field, made an
        // untouched form look edited, and could save the field blank.
        if (v === '') return;
        onValueChange(v === GENERIC_SELECT_CLEAR_VALUE ? '' : v);
      }}
      disabled={disabled}
    >
      <SelectTrigger
        id={id}
        style={triggerStyle}
        className={variant === 'compact' ? className : cn(inputStyles, className)}
      >
        <SelectValue placeholder={placeholder || 'Select'}>{renderValue}</SelectValue>
      </SelectTrigger>
      <SelectContent side="bottom" className={contentClassName}>
        {!required && (
          <SelectItem value={GENERIC_SELECT_CLEAR_VALUE} className="text-gray-400">
            {placeholder || 'Select'}
          </SelectItem>
        )}
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Dropdown genérico (2026-09-22): the sentinel a `type: 'select'` field's
 * "Select..." option carries when it's rendered as a REAL, clickable item
 * (letting the user return an optional field to unselected) instead of
 * only existing as the placeholder text shown before any pick is made.
 * Radix's <SelectItem> rejects an empty-string value outright, so this is
 * the same workaround ProjectForm.tsx's own `UNASSIGNED_VALUE` already
 * used for its assignee field -- centralized here instead of every form
 * re-inventing its own sentinel string. Only offered for fields that are
 * NOT required; a required select never gets a "Select..." item to
 * return to, by design (see the "no permitir guardar Select" rule this
 * implements).
 */
const GENERIC_SELECT_CLEAR_VALUE = '__generic_form_select_clear__';

export function GenericForm({
  title,
  headerTitle,
  mode,
  fields,
  initialValues = {},
  onSubmit,
  onCancel,
  isOpen,
  additionalSections,
  error,
  submitButtonText,
  saving = false,
  onFieldChange,
  customFooterButtons,
  footerActions,
  customLayout,
  size = 'default',
  compactWidthClassName = 'max-w-md',
  hasUnsavedChanges,
  bodyClassName,
  onDirtyChange,
}: GenericFormProps) {
  const [formData, setFormData] = useState<Record<string, any>>(initialValues);
  const formRef = useRef<HTMLFormElement>(null);
  const initialValuesRef = useRef<Record<string, any>>({});
  const previousIsOpenRef = useRef<boolean>(false);

  // Update initial values ref when form opens OR when initialValues change (for editing different items)
  useEffect(() => {
    // Form just opened (was closed, now open) OR initialValues changed while form is open
    if (isOpen && (!previousIsOpenRef.current || JSON.stringify(initialValuesRef.current) !== JSON.stringify(initialValues))) {
      // Capture the initial state when form first opens or when initialValues prop changes
      // Use the current initialValues at the moment the form opens or updates
      const deepCopy = JSON.parse(JSON.stringify(initialValues));
      initialValuesRef.current = deepCopy;
      setFormData(deepCopy);
    }
    // Update the previous state
    previousIsOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialValues]); // Trigger when isOpen changes OR when initialValues changes

  // Helper function to check if form data has changed
  const hasFormChanged = (current: Record<string, any>, initial: Record<string, any>): boolean => {
    // Compare all keys from both objects
    const currentKeys = Object.keys(current);
    const initialKeys = Object.keys(initial);
    const allKeysSet = new Set<string>();
    currentKeys.forEach(key => allKeysSet.add(key));
    initialKeys.forEach(key => allKeysSet.add(key));
    const allKeys = Array.from(allKeysSet);
    
    for (const key of allKeys) {
      const currentValue = current[key];
      const initialValue = initial[key];
      
      // Handle different value types
      // Convert both to strings for comparison, handling null/undefined
      const currentStr = currentValue == null ? '' : String(currentValue).trim();
      const initialStr = initialValue == null ? '' : String(initialValue).trim();
      
      // If values are different, form is dirty
      if (currentStr !== initialStr) {
        return true;
      }
    }
    return false;
  };

  // Reports live dirty state upward (e.g. Settings' tab bar uses this to
  // warn before switching away). customLayout forms without a
  // hasUnsavedChanges callback are reported as never-dirty, matching
  // handleCloseAttempt's own "assume no changes" fallback above.
  useEffect(() => {
    if (!onDirtyChange) return;
    if (!isOpen) {
      onDirtyChange(false);
      return;
    }
    const isDirty = hasUnsavedChanges
      ? hasUnsavedChanges()
      : !customLayout && hasFormChanged(formData, initialValuesRef.current);
    onDirtyChange(isDirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, isOpen]);

  const { ensureMembers, prompt: membershipPrompt } = useProjectMembershipPrompt();
  // The people assigned through `membershipProjectField` selects must belong to the form's project: asked ONCE, for all of them, when the form is saved
  const confirmAssignedPeople = async (data: Record<string, any>): Promise<boolean> => {
    const byProject = new Map<string, Array<{ id: string; name: string }>>();
    for (const field of fields) {
      if (!field.membershipProjectField) continue;
      const picked = data[field.name];
      const projectId = data[field.membershipProjectField];
      if (!picked || !projectId) continue;
      // a `multiselect` holds several people, a `select` one
      for (const personId of (Array.isArray(picked) ? picked : [picked])) {
        const name = field.options?.find((option) => option.value === personId)?.label || 'This person';
        byProject.set(projectId, [...(byProject.get(projectId) || []), { id: personId, name }]);
      }
    }
    for (const [projectId, people] of Array.from(byProject.entries())) if (!(await ensureMembers(projectId, people))) return false;
    return true;
  };

  const handleChange = (name: string, rawValue: any) => {
    // Normalize the "Select..." clear item back to '' -- callers never
    // need to know this sentinel exists, formData stays exactly as
    // unselected as it was before the user ever touched this field.
    const value = rawValue === GENERIC_SELECT_CLEAR_VALUE ? '' : rawValue;
    const updatedData = { ...formData, [name]: value };
    setFormData(updatedData);
    setSelectValidationError(null);

    // Call the onChange callback if provided
    if (onFieldChange) {
      onFieldChange(name, value, updatedData);
    }
  };

  // Generic required-field validation: any field marked `required` (select
  // OR text/number/date/textarea/...) can never be submitted blank. It used
  // to cover selects only, so a text field showing the red asterisk (e.g.
  // Change Order's Title) still saved empty unless the form hand-rolled its
  // own check. A number's 0 is a value, not "blank"; disabled fields are
  // skipped (the user can't fill them). Shown in the same error banner as
  // every other form error. customLayout forms render their own fields, so
  // nothing here knows their config -- they keep validating themselves.
  const [selectValidationError, setSelectValidationError] = useState<string | null>(null);

  // The server refused a write (create / update / delete) for lack of permission: the generic warning shows in the usual banner, whatever the page did with the
  // error (lib/permission-errors.ts). The form stays open and keeps what was typed. It clears on the next submit or when the form closes.
  const [permissionDenied, setPermissionDenied] = useState(false);
  useEffect(() => {
    if (!isOpen) { setPermissionDenied(false); return undefined; }
    return registerOpenForm(() => setPermissionDenied(true));
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPermissionDenied(false);
    if (!customLayout) {
      const missingMessage = firstMissingRequiredMessage(fields, formData);
      if (missingMessage) {
        setSelectValidationError(missingMessage);
        return;
      }
    }
    setSelectValidationError(null);
    // forms without person selects submit right away (no await in between)
    if (!fields.some((field) => field.membershipProjectField)) {
      onSubmit(formData);
      return;
    }
    if (!(await confirmAssignedPeople(formData))) return; // declined / not allowed: nothing is added and nothing is saved
    onSubmit(formData);
  };

  // Where the press began: a click is reported on the overlay when the press began on the form and ended outside it (the form moved or shrank under the pointer, e.g. a tab with less
  // content, or the pointer was dragged out while selecting text). That is not a click on the backdrop and must not close the form.
  const pressedOnOverlay = useRef(false);
  const handleOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    pressedOnOverlay.current = e.target === e.currentTarget;
  };
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only handle clicks on the overlay itself, not on child elements (like the form)
    // This ensures clicking outside the form triggers the close attempt
    if (e.target === e.currentTarget && pressedOnOverlay.current) {
      handleCloseAttempt();
    }
  };

  const handleCloseAttempt = () => {
    // Check for unsaved changes
    let isActuallyDirty = false;
    
    if (hasUnsavedChanges) {
      // Use the provided callback if available
      isActuallyDirty = hasUnsavedChanges();
    } else if (!customLayout) {
      // Check if form has actually changed by comparing current data to initial values
      isActuallyDirty = hasFormChanged(formData, initialValuesRef.current);
    }
    // If customLayout is used but no hasUnsavedChanges callback is provided, assume no changes
    
    if (isActuallyDirty) {
      if (window.confirm("Are you sure you want to lose all unsaved progress?")) {
        onCancel();
      }
      // If user cancels the confirmation, don't close the form
    } else {
      // No changes, close immediately
      onCancel();
    }
  };

  const fieldsByRow = fields.reduce((acc, field) => {
    const row = field.row || 999;
    if (!acc[row]) acc[row] = [];
    acc[row].push(field);
    return acc;
  }, {} as Record<number, GenericFormField[]>);

  const sortedRows = Object.keys(fieldsByRow).sort((a, b) => Number(a) - Number(b));

  if (!isOpen) return null;

  const formContent = (
    <div
      className="generic-form-overlay"
      style={{ top: 0, left: 0, right: 0, bottom: 0 }}
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
    >
      {customLayout ? (
        customLayout
      ) : (
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className={cn(
          "generic-form-container",
          size === 'compact' ? cn("w-full", compactWidthClassName) : "w-[80vw]"
        )}
        style={size === 'compact' ? { maxHeight: 'var(--form-max-height-compact)' } : { maxHeight: 'var(--form-max-height-default)' }}
      >
        {/* Header */}
        <div 
          className={cn(
            "generic-form-header",
            size === 'compact' ? "generic-form-header-compact" : "generic-form-header-default"
          )}
          style={{ backgroundColor: colors.formHeaderFooter }}
        >
          <h2 className="font-form-title">
            {headerTitle ?? (mode === 'create' ? `Create ${title}` : `Edit ${title}`)}
          </h2>
          <CloseButton onClick={handleCloseAttempt} />
        </div>

        {/* Body */}
        <div className={cn(
          "generic-form-body",
          size === 'compact' ? "generic-form-body-compact" : "generic-form-body-default",
          bodyClassName
        )}>
            {(permissionDenied || error || selectValidationError) && (
              <div className="generic-form-error">
                {permissionDenied ? INSUFFICIENT_PERMISSIONS_MESSAGE : (error || selectValidationError)}
              </div>
            )}
          <div className={cn(size === 'compact' ? "generic-form-body-spacing-compact" : "generic-form-body-spacing-default")}>
            {sortedRows.map((rowKey) => {
              const rowFields = fieldsByRow[Number(rowKey)];
              const rowMaxWidth = rowFields[0]?.rowMaxWidth;
              // Check if any field in this row uses 0.5 colSpan (quarter width)
              const hasQuarterWidth = rowFields.some(f => f.colSpan === 0.5);
              const gridCols = hasQuarterWidth ? "md:grid-cols-4" : "md:grid-cols-2";
              // Check if any field in this row has labelPosition: 'left' (for centering)
              const hasLeftLabel = rowFields.some(f => f.labelPosition === 'left');
              return (
                <div
                  key={rowKey}
                  className={cn(
                    "grid grid-cols-1",
                    size === 'compact' ? "gap-4" : "gap-6",
                    gridCols,
                    hasLeftLabel && "justify-items-center"
                  )}
                  style={rowMaxWidth ? { maxWidth: rowMaxWidth } : undefined}
                >
                {rowFields.map((field) => {
                  if (field.type === 'checkbox') {
                    // A boolean field: the label sits next to the box, no
                    // separate label row. Value is a real boolean in formData.
                    return (
                      <div key={field.name} className={cn('flex items-center gap-2', field.colSpan === 2 && 'md:col-span-2')}>
                        <Checkbox
                          id={field.name}
                          checked={formData[field.name] === true}
                          disabled={field.disabled}
                          onCheckedChange={(checked) => handleChange(field.name, checked)}
                        />
                        <Label htmlFor={field.name} className="font-form-label mb-0">{field.label}</Label>
                        {field.helperText && <span className="text-xs text-gray-500">{field.helperText}</span>}
                      </div>
                    );
                  }
                  const isLeftLabel = field.labelPosition === 'left';
                  return (
                  <div
                    key={field.name}
                    className={cn(
                        isLeftLabel ? "flex items-center gap-2 justify-center" : "space-y-2",
                        field.colSpan === 2 ? "md:col-span-2" : 
                        field.colSpan === 0.5 ? "md:col-span-1" :
                        hasQuarterWidth ? "md:col-span-1" : "md:col-span-1"
                    )}
                  >
                    <Label htmlFor={field.name} className={cn("font-form-label", isLeftLabel && "mb-0", "whitespace-nowrap")}>
                      {field.label}<RequiredMark required={field.required && !field.hideRequiredMark} />
                    </Label>

                    {field.type === 'multiselect' ? (
                      <>
                      <GenericCheckboxList
                        id={field.name}
                        options={(field.options || []) as CheckboxListOption[]}
                        value={Array.isArray(formData[field.name]) ? formData[field.name] : []}
                        onChange={(next) => handleChange(field.name, next)}
                        disabled={field.disabled}
                        filters={field.filters}
                        searchPlaceholder={field.placeholder}
                      />
                        {field.helperText && (
                          <div className="text-xs text-gray-500 mt-1">
                            {field.helperText}
                          </div>
                        )}
                      </>
                    ) : field.type === 'select' ? (
                      <>
                      <GenericSelect
                        id={field.name}
                        value={formData[field.name] || ''}
                        onValueChange={(value) => handleChange(field.name, value)}
                        disabled={field.disabled}
                        required={field.required}
                        placeholder={field.placeholder}
                        options={field.options || []}
                        searchable={!!field.membershipProjectField}
                        contentClassName={field.name === 'project_id' ? '!overflow-y-auto max-h-[15rem] [&>div]:!h-auto [&>div]:!max-h-[15rem]' : (field.name === 'country' || field.name === 'assignedTo' || (field.options && field.options.length > 5) ? 'max-h-[18.75rem] overflow-y-auto' : '')}
                      />
                        {field.helperText && (
                          <div className="text-xs text-gray-500 mt-1">
                            {field.helperText}
                          </div>
                        )}
                      </>
                    ) : field.type === 'textarea' ? (
                      <>
                      <Textarea
                        id={field.name}
                        value={formData[field.name] || ''}
                        onChange={(e) => handleChange(field.name, e.target.value)}
                        placeholder={field.placeholder}
                        disabled={field.disabled}
                        rows={field.rows}
                        compact={field.rows !== undefined}
                      />
                        {field.helperText && (
                          <div className="text-xs text-gray-500 mt-1">
                            {field.helperText}
                          </div>
                        )}
                      </>
                    ) : field.type === 'date' ? (
                      <>
                      <DateInput
                        id={field.name}
                        value={formData[field.name] || ''}
                        onChange={(iso) => handleChange(field.name, iso)}
                        placeholder={field.placeholder}
                        className={inputStyles}
                        disabled={field.disabled}
                      />
                        {field.helperText && (
                          <div className="text-xs text-gray-500 mt-1">
                            {field.helperText}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                      <Input
                        id={field.name}
                        type={field.type}
                        value={field.displayValue !== undefined ? field.displayValue : (formData[field.name] || '')}
                        onChange={(e) => handleChange(field.name, e.target.value)}
                        placeholder={field.placeholder}
                          className={field.type === 'number'
                            ? `${inputStyles} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`
                            : inputStyles
                          }
                        disabled={field.disabled || field.displayValue !== undefined}
                        step={field.type === 'number' ? '0.01' : undefined}
                          onKeyDown={(e) => {
                            // Prevent arrow keys from changing number values
                            if (field.type === 'number' && ['ArrowUp', 'ArrowDown'].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onWheel={(e) => {
                            // Prevent trackpad/mouse wheel from changing number values
                            if (field.type === 'number') {
                              e.preventDefault();
                              e.stopPropagation();
                            }
                          }}
                          onFocus={(e) => {
                            // Prevent wheel events on focused number inputs
                            if (field.type === 'number') {
                              e.currentTarget.addEventListener('wheel', (event) => {
                                event.preventDefault();
                              }, { passive: false });
                            }
                          }}
                        />
                        {field.helperText && (
                          <div className="text-xs text-gray-500 mt-1">
                            {field.helperText}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  );
                })}
              </div>
              );
            })}
          </div>

          {/* Additional sections (comments, attachments, etc.) */}
          {additionalSections && (
            <div className="space-y-6 mt-6">
              {typeof additionalSections === 'function' ? additionalSections(formData) : additionalSections}
            </div>
          )}
        </div>

        {/* Footer */}
          {customFooterButtons ? (
            <div 
              className={cn(
                "generic-form-footer",
                size === 'compact' ? "generic-form-footer-compact" : "generic-form-footer-default"
              )}
              style={{ backgroundColor: colors.formHeaderFooter }}
            >
              {customFooterButtons}
            </div>
          ) : (
        <div 
          className={cn(
            "generic-form-footer",
            size === 'compact' ? "generic-form-footer-compact" : "generic-form-footer-default"
          )}
          style={{ backgroundColor: colors.formHeaderFooter }}
        >
          <CancelButton onClick={handleCloseAttempt} />
          {footerActions}
          <SaveChangesButton disabled={saving}>
            {saving ? 'Saving...' : (submitButtonText || (mode === 'create' ? 'Create' : 'Save Changes'))}
          </SaveChangesButton>
        </div>
          )}
      </form>
      )}
    </div>
  );

  // Render in a portal to ensure it's at the document body level
  // This ensures fixed positioning works correctly and covers the entire screen
  return (
    <>
      {createPortal(formContent, document.body)}
      {membershipPrompt}
    </>
  );
}
